import * as Sentry from "@sentry/react";
import axios from "axios";

type SentryContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

type SentryUser = {
  id?: string | number;
  email?: string;
  username?: string;
  accountId?: string | number;
  clusterId?: string | number;
};

type ErrorLike = {
  name?: unknown;
  code?: unknown;
  __CANCEL__?: unknown;
};

export const isRequestCancellation = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;

  const { name, code, __CANCEL__ } = error as ErrorLike;

  return (
    code === "ERR_CANCELED" ||
    name === "CanceledError" ||
    name === "AbortError" ||
    __CANCEL__ === true
  );
};

const isAxiosError = (error: unknown): boolean => axios.isAxiosError(error);

const hasAxiosException = (event: Sentry.Event): boolean =>
  event.exception?.values?.some(({ type }) => type === "AxiosError") ?? false;

// const sentryDsn = process.env.REACT_APP_SENTRY_DSN || "";
// const sentryEnvironment =
//   process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV ;
// const sentryRelease = process.env.REACT_APP_SENTRY_RELEASE || undefined;
// const tracesSampleRate = Number(
//   process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0
// );
// const replaysSessionSampleRate = Number(
//   process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0
// );
// const replaysOnErrorSampleRate = Number(
//   process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1
// );

const sentryDsn =
  "https://a4b8305d5338433c97aacbbf37d71292@o4511224268062720.ingest.us.sentry.io/4511550904664064";
const sentryEnvironment = "production";
const sentryRelease = "1.0.0";
const tracesSampleRate = Number("0.1");
const replaysSessionSampleRate = Number("1.0");
const replaysOnErrorSampleRate = Number("1.0");

let initialized = false;

const getSampleRate = (value: number, fallback: number) =>
  Number.isNaN(value) ? fallback : value;

const isSentryEnabled = () => {
  if (!sentryDsn) return false;
  // return false
  if (sentryEnvironment !== "production") return false;

  return true;
};

export const initSentry = () => {
  if (initialized || !isSentryEnabled()) return;

  return;

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: getSampleRate(tracesSampleRate, 0),
    replaysSessionSampleRate: getSampleRate(replaysSessionSampleRate, 0),
    replaysOnErrorSampleRate: getSampleRate(replaysOnErrorSampleRate, 1),
    beforeSend(event, hint) {
      if (
        isRequestCancellation(hint?.originalException) ||
        isAxiosError(hint?.originalException) ||
        hasAxiosException(event)
      ) {
        return null;
      }

      return event;
    },
  });

  Sentry.setTag("app", "dcdial-customer-portal");
  initialized = true;
};

export const captureException = (
  error: unknown,
  context: SentryContext = {}
) => {
  if (
    !isSentryEnabled() ||
    isRequestCancellation(error) ||
    isAxiosError(error)
  ) {
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      scope.setTags(context.tags);
    }

    if (context.extra) {
      scope.setExtras(context.extra);
    }

    Sentry.captureException(error);
  });
};

export const setSentryUser = (user?: SentryUser | null) => {
  if (!isSentryEnabled()) return;

  if (!user?.id && !user?.email && !user?.username) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id ? String(user.id) : undefined,
    email: user.email,
    username: user.username,
    account_id: user.accountId ? String(user.accountId) : undefined,
    cluster_id: user.clusterId ? String(user.clusterId) : undefined,
  });
};
